import React from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, TwitterIcon, InstagramIcon, FacebookIcon, InfoIcon } from 'lucide-react';
import { useBrand } from '../../hooks/useBrand';
import type { BrandLink } from '../../data/brandContent';
const SOCIAL_LINKS = [{
  key: 'twitter',
  label: 'Twitter',
  icon: TwitterIcon
}, {
  key: 'instagram',
  label: 'Instagram',
  icon: InstagramIcon
}, {
  key: 'facebook',
  label: 'Facebook',
  icon: FacebookIcon
}] as const;

/**
 * Footer — one structure for both platforms. Columns, copy and the sister-site
 * link come from brand content; colours from the brand theme.
 */
const Footer = () => {
  const {
    theme,
    content
  } = useBrand();
  const f = theme.footer;
  return <footer className={f.root} data-id="element-1965" data-mp="1uf3dwdxvvn05">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1966" data-mp="1ubny18wdy9xo">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-1967" data-mp="1pde2gx6r17bj">
          {/* Brand column */}
          <div data-id="element-1968" data-mp="1ledlzlbqugrp">
            <Link to={content.homePath} className="flex items-center" data-id="element-1969" data-mp="1ldvsgwggugnh">
              {f.showWordmarkIcon && <BookIcon className="h-8 w-8 text-purple-400 mr-2" data-id="element-1970" data-mp="29vdprk9i37rm" />}
              <span className={f.wordmark} data-id="element-1971" data-mp="2eprqm26xm73o">{content.name}</span>
            </Link>
            <p className={`mt-4 text-sm ${f.tagline}`} data-id="element-1972" data-mp="200lq8awcyau7">
              {content.footer.tagline}
            </p>
            <div className="mt-6 flex space-x-4" data-id="element-1973" data-mp="24ywzajetwhbd">
              {SOCIAL_LINKS.map(({
              key,
              label,
              icon: Icon
            }) => <a key={key} href={content.footer.social[key]} target="_blank" rel="noopener noreferrer" aria-label={`${content.name} on ${label}`} className={f.socialLink} data-id="element-1974" data-mp="2slvcn87nj952">
                  <Icon className="h-5 w-5" data-id="element-1975" data-mp="2nqy5y7xuhbvc" />
                </a>)}
            </div>
          </div>

          {content.footer.columns.map(column => <div key={column.heading} data-id="element-1976" data-mp="1cahm82f9c92o">
              <h3 className={`text-sm font-semibold uppercase tracking-wider ${f.heading}`} data-id="element-1977" data-mp="1c7ddqsurer67">
                {column.heading}
              </h3>
              <ul className="mt-4 space-y-2" data-id="element-1978" data-mp="39y2ado7tzr0n">
                {column.links.map(link => <li key={link.to} data-id="element-1979" data-mp="354zbr1o75rdl">
                    <FooterLink link={link} className={f.link} data-id="element-1980" data-mp="307h2tba6jc5l" />
                  </li>)}
              </ul>
            </div>)}
        </div>

        {/* Cross-platform sister site */}
        <div className={`mt-12 pt-8 border-t ${f.dividerSoft} flex justify-center`} data-id="element-1981" data-mp="2r72es45tziye">
          <Link to={content.footer.sisterSite.to} className={`${f.sisterLink} text-sm flex items-center transition-colors group`} data-id="element-1982" data-mp="2m7pvtui2yw13">
            <span className={`${f.sisterGlyph} mr-2 transition-colors`} aria-hidden="true" data-id="element-1983" data-mp="2hafx1o8sx80l">
              ✦
            </span>
            {content.footer.sisterSite.lead}{' '}
            <span className="font-semibold mx-1" data-id="element-1984" data-mp="fijmagugk26v">
              {content.footer.sisterSite.name}
            </span>
            {content.footer.sisterSite.suffix}
          </Link>
        </div>

        <div className={`mt-8 border-t ${f.divider} pt-8 text-sm text-center ${f.copyright}`} data-id="element-1985" data-mp="rysrls63nn9b">
          <p data-id="element-1986" data-mp="wr421oygurg4">
            &copy; {new Date().getFullYear()} {content.name}. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>;
};
const FooterLink = ({
  link,
  className
}: {
  link: BrandLink;
  className: string;
}) => <Link to={link.to} className={`${className} text-sm flex items-center ${link.emphasis ? 'font-medium' : ''}`} data-id="element-1987" data-mp="90qas0npgwml">
    {link.label}
    {link.hint && <span className="ml-1 group relative" data-id="element-1988" data-mp="e25unpe5cx3e">
        <InfoIcon className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" data-id="element-1989" data-mp="izfrhq3v8b19" />
        <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-800 text-xs text-white py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity" data-id="element-1990" data-mp="1pf86y897r0hr">
          {link.hint}
        </span>
      </span>}
  </Link>;
export default Footer;